import { useSpaceIDFromPath } from '@/data/space/hooks';
import { useFeatureFlag, useGetSpaceQuery } from '@/store';
import flags from '@/store/flags';
import { ArrowRight } from '@phosphor-icons/react';
import { XCircleFill } from '@styled-icons/bootstrap';
import { Button } from '@typedream/ui';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import CheckboxItem from '../../../sharedComponents/Options/CheckboxItem';
import sharedStyles from '../../../styles/Shared.module.css';
import { Dropdown, DropdownItem } from '../../Dropdown/Dropdown';
import { TEMPLATE_SITE_TYPE_NAME } from '../../SiteOptions/types';
import UploadImage from '../../UploadImage';
import styles from '../SaveTemplatePopup.module.css';
import { SaveTemplatePopupContext } from '../contextProvider';
import { SAVE_TEMPLATE_PAGE } from '../types';

interface IInputErr {
  name?: boolean;
  category?: boolean;
  preview?: boolean;
  description?: boolean;
}

function Create() {
  const router = useRouter();
  const [name, setname] = useState('');
  const [categories, setcategories] = useState({});
  const [previewImageFile, setpreviewImageFile] = useState(null);
  const [description, setdescription] = useState('');
  const [inputErr, setinputErr] = useState<IInputErr>({});
  const { err, setstep, onCreateTemplate, templates, categoryOptions, pageID } =
    useContext(SaveTemplatePopupContext);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitMarketLoading, setSubmitMarketLoading] = useState(false);
  const spaceID = useSpaceIDFromPath();
  const { data: space } = useGetSpaceQuery([spaceID], { skip: !spaceID });
  const templateMarketplaceFlag = useFeatureFlag(flags.TemplateMarketplace);

  const validateInput = () => ({
    name: !name,
    category: !categories || Object.values(categories).every((x) => !x),
    preview: !previewImageFile,
    description: !description,
  });

  useEffect(() => {
    setinputErr({ ...inputErr, name: false });
  }, [name]);
  useEffect(() => {
    setinputErr({ ...inputErr, category: false });
  }, [categories]);
  useEffect(() => {
    setinputErr({ ...inputErr, description: false });
  }, [description]);
  useEffect(() => {
    setinputErr({ ...inputErr, preview: false });
  }, [previewImageFile]);

  const onSubmit = async () => {
    setSubmitLoading(true);
    const inErr = validateInput();
    if (Object.values(inErr).some((x) => x)) {
      setinputErr(inErr);
      setSubmitLoading(false);
      return;
    }
    const categoriesList = Object.entries(categories)
      ?.map(([key, value]) => (value ? key : undefined))
      .filter((x) => x);

    await onCreateTemplate({
      name,
      description,
      category: categoriesList,
      previewImageFile,
    });
    setSubmitLoading(false);
  };

  const onSubmitMarketPlace = async () => {
    setSubmitMarketLoading(true);
    const inErr = validateInput();
    if (Object.values(inErr).some((x) => x)) {
      setinputErr(inErr);
      setSubmitMarketLoading(false);
      return;
    }
    const categoriesList = Object.entries(categories)
      ?.map(([key, value]) => (value ? key : undefined))
      .filter((x) => x);

    const res = await onCreateTemplate({
      name,
      description,
      category: categoriesList,
      previewImageFile,
      pid: pageID,
    });

    if (!res) {
      setSubmitMarketLoading(false);
      return;
    }

    router.push(`/settings/${space.slug}/${spaceID}/templates/${res.id}`);
  };

  const onRemoveCard = () => {
    setpreviewImageFile(null);
  };

  const dropdownTriggerLabel =
    categories && Object.values(categories).filter((x) => x)?.length > 0 ? (
      <div
        className={classNames(
          'is-flex',
          'has-fullwidth',
          'is-align-items-center',
          'is-justify-content-space-between'
        )}
      >
        <div className={sharedStyles.tagInput}>
          {Object.entries(categories)?.map(
            ([k, v]) =>
              v && (
                <div className="tags has-addons m-1">
                  <span
                    className={classNames('tag')}
                    style={{ backgroundColor: 'var(--background-popup-overlay)' }}
                  >
                    {TEMPLATE_SITE_TYPE_NAME[k] || k}
                  </span>
                  <span
                    className={classNames('tag is-delete')}
                    role="none"
                    style={{ backgroundColor: 'var(--background-popup-overlay)' }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setcategories((prevCategories) => ({
                        ...prevCategories,
                        [k]: false,
                      }));
                    }}
                  />
                </div>
              )
          )}
        </div>
        <XCircleFill
          size={17}
          className="ml-2 with-lighter-text-color"
          onClick={() => setcategories({})}
        />
      </div>
    ) : (
      <div className={classNames(sharedStyles.tagInput, 'with-lighter-text-color py-2')}>
        Select Category
      </div>
    );

  return (
    <div>
      {templates?.length > 0 && (
        <div
          className="is-subtitle mb-4 with-cursor-pointer"
          role="none"
          onClick={() => {
            setstep(SAVE_TEMPLATE_PAGE.EXISTING_OR_NEW);
          }}
        >
          &larr; Back
        </div>
      )}
      <div className={classNames('is-size-5 with-text-color mb-3')}>Save Template</div>
      <div className="is-subtitle mb-6">Save this page as a template that you can reuse later.</div>
      <div className="mb-4">
        <div className="is-size-6 with-text-color mb-1">Template Name</div>
        <input
          className={classNames(sharedStyles.inputMedium, 'has-fullwidth', {
            [sharedStyles.inputError]: inputErr.name,
          })}
          type="text"
          value={name}
          placeholder="e.g. Cardy – Landing Page Template"
          onChange={(e) => setname(e.target.value)}
        />
      </div>
      <div className="mb-4">
        <div className="is-size-6 with-text-color mb-1">Category</div>
        <Dropdown multiple triggerLabel={dropdownTriggerLabel}>
          {categoryOptions?.map((c) => (
            <DropdownItem>
              <div
                role="none"
                onClick={() => {
                  setcategories((prevCategories) => ({
                    ...prevCategories,
                    [c]: !prevCategories[c],
                  }));
                }}
              >
                <CheckboxItem
                  title={<p className="with-text-color">{TEMPLATE_SITE_TYPE_NAME[c] || c}</p>}
                  selected={categories?.[c]}
                  className={classNames(styles.checkboxItem, 'with-cursor-pointer')}
                  checkboxSize={13}
                />
              </div>
            </DropdownItem>
          ))}
        </Dropdown>
      </div>
      <div className="mb-4">
        <div className="is-size-6 with-text-color mb-1">Template Thumbnail</div>
        <div className="is-small-subtitle mb-2">
          Upload a 1200 x 1440 pixel PNG or JPG preview of the template.
        </div>
        <UploadImage
          setfile={setpreviewImageFile}
          file={previewImageFile}
          containerWidth={240}
          containerHeight={288}
          width={224}
          height={272}
          borderRadius={4}
          className={classNames(styles.previewImageContainer, {
            [sharedStyles.inputError]: inputErr.preview,
          })}
          placeholderImage="/images/add_template_preview_image.svg"
          onRemoveCard={onRemoveCard}
        />
      </div>

      <div className="mb-4">
        <div className="is-subtitle-size with-text-color mb-2">Template Description</div>
        <textarea
          className={classNames(sharedStyles.inputMedium, styles.textarea, 'has-fullwidth', {
            [sharedStyles.inputError]: inputErr.description,
          })}
          value={description}
          placeholder="Add some description about this template."
          rows={2}
          onChange={(e) => setdescription(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button
          size="xs"
          onClick={onSubmit}
          isLoading={submitLoading}
          isDisabled={submitMarketLoading}
        >
          Save Template
        </Button>
        {templateMarketplaceFlag && (
          <Button
            size="xs"
            onClick={onSubmitMarketPlace}
            isLoading={submitMarketLoading}
            isDisabled={submitLoading}
            rightIcon={<ArrowRight size={12} className="align-top" />}
            variant="secondary"
          >
            Save & Submit to Marketplace
          </Button>
        )}
      </div>
      {err && <div className="with-danger-text-color">{err}</div>}
    </div>
  );
}

export default Create;
